export type LocaleCode = string;

export type Locale = {
  code: LocaleCode;
  /** Shown in the language menu, written in the language itself. */
  label: string;
  /**
   * A testing locale: reachable with `?lng=`, never listed in the menu. Keeps
   * the pseudo-locale usable in every environment without offering it to
   * customers.
   */
  development?: boolean;
};

/**
 * The locales the app offers.
 *
 * A locale only belongs here once its files exist under
 * `localizations/locales/<code>/`. With one entry the language menu renders
 * nothing, which keeps the feature invisible.
 */
export const SUPPORTED_LOCALES: readonly Locale[] = [
  { code: 'en', label: 'English' },
  { code: 'en-PI', label: 'Pirate', development: true },
  // Reachable for review while the translation is in progress; listed in the
  // menu once it is reviewed.
  { code: 'ko', label: '한국어', development: true },
];

/** The locales a person can pick from the menu. */
export const selectableLocales = (): readonly Locale[] =>
  SUPPORTED_LOCALES.filter((locale) => !locale.development);

export const DEFAULT_LOCALE: LocaleCode = 'en';

export const isSupportedLocale = (value: string | null | undefined): boolean =>
  !!value && SUPPORTED_LOCALES.some((locale) => locale.code === value);

/**
 * Matches a browser tag like `ko-KR` against a locale, preferring an exact
 * match over the base language so a tag like `en-PI` is never swallowed by
 * `en`.
 *
 * `locales` is a parameter so the precedence can be tested against a list that
 * actually contains a region-tagged entry. Testing it against the live registry
 * proves nothing while that registry holds a single locale.
 */
export const matchSupportedLocale = (
  tag: string,
  locales: readonly Locale[] = SUPPORTED_LOCALES,
): LocaleCode | undefined => {
  const exact = locales.find((locale) => locale.code === tag);
  if (exact) {
    return exact.code;
  }

  const base = tag.split('-')[0];
  return locales.find((locale) => locale.code === base)?.code;
};
